import { useRef, useImperativeHandle, forwardRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

/**
 * CaptchaWidget — renders inline hCaptcha challenge only if a sitekey is configured.
 * When REACT_APP_HCAPTCHA_SITEKEY is empty (dev / not configured) the widget renders
 * nothing and reports a null token; the backend dependency `verify_hcaptcha` is also
 * a no-op in that state, so forms work end-to-end without a captcha.
 *
 * The parent should call `.reset()` on the ref after submit (win or fail) to force
 * a fresh challenge on the next attempt.
 */
export const CaptchaWidget = forwardRef(function CaptchaWidget({ onToken }, ref) {
  const cap = useRef(null);
  const sitekey = process.env.REACT_APP_HCAPTCHA_SITEKEY;

  useImperativeHandle(ref, () => ({
    reset: () => cap.current?.resetCaptcha(),
    isEnabled: () => Boolean(sitekey),
  }), [sitekey]);

  if (!sitekey) return null;

  return (
    <div className="my-3 flex justify-center" data-testid="hcaptcha-widget">
      <HCaptcha
        ref={cap}
        sitekey={sitekey}
        onVerify={(token) => onToken(token)}
        onExpire={() => onToken(null)}
        onError={() => onToken(null)}
      />
    </div>
  );
});
